import { Brand, Country, Product } from 'api';
import { CartItem } from 'components/Cart/Cart';
import { cloneDeep } from 'lodash';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useUserContext } from "context/data/userContext";

interface IMobileContext {
  brand?: Brand,
  pic?: string,
  setBrand: (brand?: Brand) => void,
  country?: Country,
  setCountry: (country?: Country) => void,
  dateTime: Date | null,
  setDateTime: (date: Date | null) => void,
  cart: CartItem[],
  setCart: (cart: CartItem[]) => void,
  addItem: (product: Product) => void,
  removeItem: (product: Product) => void,
  updateDiscardDate: (product: Product, date: Date | null) => void,
}

export const MobileContext = React.createContext({} as IMobileContext);

export function useCreateMobileContext(): IMobileContext {
  const { user } = useUserContext();
  const [brand, setBrand] = useState<any>();
  const [country, setCountry] = useState<any>();
  const [dateTime, setDateTime] = useState<Date | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const pic = useMemo(() => {
    return `${user.firstName} ${user.lastName?.[0]}`
  },[user]);
  const addItem = useCallback((product: Product, newQuantity: number = 1) => {
    const index = cart.findIndex(({ id }) => id === product.id);
    if (index === -1) {
      setCart([
        ...cart,
        { ...product, quantity: newQuantity },
      ]);
    } else {
      const newCart = cloneDeep(cart);

      newCart[index].quantity = newQuantity;

      if (newCart[index].quantity === 0) {
        newCart.splice(index, 1);
      }
      setCart(newCart);
    }
  }, [setCart, cart]);
  const removeItem = useCallback((product: Product, newQuantity: number = 1) => {
    const index = cart.findIndex(({ id }) => id === product.id);
    if (index !== -1) {
      const newCart = cloneDeep(cart);

      newCart[index].quantity = newQuantity;
      if (newCart[index].quantity === 0) {
        newCart.splice(index, 1);
      }
      setCart(newCart);
    }
  }, [setCart, cart]);
  const updateDiscardDate = useCallback((product: Product, discardDate: Date | null) => {
    const index = cart.findIndex(({ id }) => id === product.id);
    if (index !== -1) {
      const newCart = cloneDeep(cart);
      newCart[index].discardDate = discardDate;
      setCart(newCart);
    }
  }, [setCart, cart]);

  const mobileContext = useMemo(() => ({
    brand,
    setBrand,
    country,
    setCountry,
    dateTime,
    setDateTime,
    cart,
    setCart,
    addItem,
    removeItem,
    updateDiscardDate,
    pic,
  }), [brand, setBrand, country, setCountry, dateTime, setDateTime, cart, setCart, addItem, removeItem, updateDiscardDate, pic]);

  return mobileContext;
}

export function MobileContextProvider({ children }: { children: JSX.Element }): JSX.Element {
  const mobileContext = useCreateMobileContext();

  return (
    <MobileContext.Provider value={mobileContext}>
      {children}
    </MobileContext.Provider>
  );
}

export function useMobileContext(): IMobileContext {
  return useContext(MobileContext);
}
